// The steps source on the F4 API (core/api/src/routes/steps.ts): department steps and their rules per
// production, the state of each step per episode with the server's finals gate, sign-off requests and decisions,
// what waits on you, and the production templates. The mapping functions are pure (tested in v2-steps.test.ts);
// `createStepsSource` binds them to an Api and a workspace and puts names on the user ids.
import type { Api } from "../api.ts";
import { type Member, memberName } from "../collab/reviewApi.ts";
import { DEPARTMENT_LABEL, type Department } from "../departments.ts";
import { type ApiItemAssignment, itemsFromSteps } from "./itemCrew.ts";
import { loadMembers } from "./members.ts";
import type { EpisodeSignoffs, StepsSource, Template, WaitingItem } from "./sources.ts";
import {
  type DeptSignoff,
  listNames,
  type RuleMode,
  type ServerGate,
  type SignoffRule,
  type StepWord,
} from "./steps.ts";

// ── the API's shapes (core/api/src/schemas-steps.ts) ──────────────────────────────────────────────────────

export interface ApiStep {
  id: string;
  project_id: string;
  department: Department;
  depends_on: Department[];
  skipped: boolean;
  signoff_mode: RuleMode;
  /** empty: a director or higher signs off */
  signers: string[];
  signer_role: string | null;
  locks_finals: boolean;
  workers: string[];
  /** live assignments on one item of the step (a cast member, set, prop, scene or shot) */
  items: ApiItemAssignment[];
  updated_at: string;
}

interface ApiWord {
  by: string | null;
  at: string;
  note: string | null;
}

export interface ApiEpisodeStep extends ApiStep {
  state: "signed" | "waiting" | "changes" | "open" | "skipped";
  signed_by: string[];
  waiting_on: string[];
  waiting_on_me: boolean;
  can_sign: boolean;
  request: (ApiWord & { id: string }) | null;
  changes: ApiWord | null;
  stale_since: string | null;
  /** a token for the last change the sign-off is about; sent back as `expectVersion` when signing off */
  version: string | null;
}

export interface ApiEpisodeSteps {
  episode_id: string;
  project_id: string;
  steps: ApiEpisodeStep[];
  gate: { open: boolean; waiting_on: Department[] };
}

export interface ApiWaitingItem {
  episode_id: string;
  episode_number: number;
  episode_title: string | null;
  project_id: string;
  project_name: string;
  department: Department;
  requested_by: string | null;
  requested_at: string;
  note: string | null;
  signed_by: string[];
  /** what is up for sign-off, as a label ("Script v3", "14 shots") */
  version_label?: string | null;
  /** the step's version token (sent back as `expectVersion`) */
  version: string | null;
}

export interface ApiTemplate {
  id: string;
  name: string;
  type: string;
  aspect: string;
  blurb: string;
  skipped: Department[];
  locks: Department[];
  roles: Record<string, string>;
}

/** The body of PUT /projects/{id}/steps/{dept}. */
export interface StepPatch {
  signoffMode?: RuleMode;
  signers?: string[];
  signerRole?: string | null;
  locksFinals?: boolean;
  skipped?: boolean;
  workers?: string[];
}

// ── mapping (pure) ────────────────────────────────────────────────────────────────────────────────────────

/** A step as the Crew screen's rule. */
export function ruleFromStep(s: ApiStep): SignoffRule {
  return {
    dept: s.department,
    workers: [...s.workers],
    signers: [...s.signers],
    mode: s.signoff_mode,
    ...(s.signer_role ? { role: s.signer_role } : {}),
    locksFinals: s.locks_finals,
    skipped: s.skipped,
    ...(s.items?.length ? { items: itemsFromSteps([s]) } : {}),
  };
}

/** A rule as the PUT body: the whole rule, so the row on the server is what the screen shows. */
export function stepPatch(rule: SignoffRule): StepPatch {
  return {
    signoffMode: rule.mode,
    signers: [...new Set(rule.signers)],
    signerRole: rule.mode === "role" ? (rule.role ?? "director") : null,
    // the final never locks itself (SQL check)
    ...(rule.dept === "final" ? {} : { locksFinals: rule.locksFinals }),
    skipped: !!rule.skipped,
    workers: [...new Set(rule.workers)],
  };
}

const word = (w: ApiWord | null, nameOf: (id: string) => string): StepWord | null =>
  w ? { by: w.by ? nameOf(w.by) : null, at: w.at, note: w.note } : null;

/** One episode step as the board reads it, with names for the people. */
export function signoffFromStep(s: ApiEpisodeStep, nameOf: (id: string) => string): DeptSignoff {
  return {
    state: s.state,
    waitingOn: s.waiting_on.map(nameOf),
    waitingOnMe: s.waiting_on_me,
    canSign: s.can_sign,
    signedBy: s.signed_by.map(nameOf),
    request: word(s.request, nameOf),
    changes: word(s.changes, nameOf),
    staleSince: s.stale_since,
    version: s.version ?? null,
    rule: ruleFromStep(s),
  };
}

export const gateFromApi = (g: ApiEpisodeSteps["gate"]): ServerGate => ({
  open: g.open,
  waitingOn: [...g.waiting_on],
});

/** GET /episodes/{id}/steps as the board's sign-offs, the server's gate, the locks and who works where. */
export function episodeFromApi(res: ApiEpisodeSteps, nameOf: (id: string) => string): EpisodeSignoffs {
  const signoffs: Partial<Record<Department, DeptSignoff>> = {};
  const workers: Partial<Record<Department, string[]>> = {};
  for (const s of res.steps) {
    signoffs[s.department] = signoffFromStep(s, nameOf);
    if (s.workers.length) workers[s.department] = [...s.workers];
  }
  return {
    signoffs,
    gate: gateFromApi(res.gate),
    locks: res.steps.filter((s) => s.locks_finals && !s.skipped).map((s) => s.department),
    workers,
    items: itemsFromSteps(res.steps),
  };
}

/** "Episode 2" or "Episode 2: The Long Night". */
const episodeLabel = (n: number, title: string | null) => {
  const t = title?.trim();
  return t && !/^(episode|ep\.?)\s*\d+$/i.test(t) ? `Episode ${n}: ${t}` : `Episode ${n}`;
};

/** One item of GET /waiting as Home and the mobile queue show it ("Joe asked for your sign-off"). */
export function waitingFromApi(w: ApiWaitingItem, nameOf: (id: string) => string): WaitingItem {
  const by = w.requested_by ? nameOf(w.requested_by) : null;
  const signed = w.signed_by.map(nameOf);
  // a server before version_label had the label in `version` and no token
  const label = "version_label" in w ? w.version_label : w.version;
  const token = "version_label" in w ? w.version : null;
  return {
    id: `${w.episode_id}:${w.department}`,
    dept: w.department,
    title: episodeLabel(w.episode_number, w.episode_title),
    versionLabel: label ?? "",
    version: token ?? null,
    projectId: w.project_id,
    production: w.project_name,
    episodeId: w.episode_id,
    line: [
      by && by !== "you"
        ? `${by} asked for your sign-off`
        : signed.length
          ? null
          : `${DEPARTMENT_LABEL[w.department]} waits for your sign-off`,
      signed.length ? `${listNames(signed)} signed off` : null,
    ]
      .filter(Boolean)
      .join(" · "),
    note: w.note,
    at: w.requested_at,
    frame: null,
    changed: null,
    signedBy: signed,
    kind: "review",
  };
}

export const templateFromApi = (t: ApiTemplate): Template => ({
  id: t.id,
  name: t.name,
  type: t.type,
  aspect: t.aspect,
  blurb: t.blurb,
  skipped: [...t.skipped],
  still: null,
});

/** Names for user ids: "you" for the signed-in person, the member's name, else "Someone". */
export function namer(members: readonly Member[], meId: string | null | undefined): (id: string) => string {
  const m = new Map(members.map((x) => [x.user_id, memberName(x)]));
  return (id) => (meId && id === meId ? "you" : (m.get(id) ?? "Someone"));
}

/** The body of a decision: the note when there is one, the version the person saw when it is known. */
export function signoffBody(
  decision: string,
  note?: string | null,
  version?: string | null,
): { decision: string; note?: string; expectVersion?: string } {
  return {
    decision,
    ...(note?.trim() ? { note: note.trim() } : {}),
    ...(version ? { expectVersion: version } : {}),
  };
}

// ── the source ────────────────────────────────────────────────────────────────────────────────────────────

export function createStepsSource(api: Api, ws: string, meId: string | null | undefined): StepsSource {
  const w = `/workspaces/${ws}`;
  const names = () =>
    loadMembers(api, ws).then(
      (m) => namer(m, meId),
      () => namer([], meId),
    );
  const projectSteps = (projectId: string) =>
    api.request<{ items: ApiStep[] }>("GET", `${w}/projects/${projectId}/steps`).then((r) => r.items);
  const decide = async (
    episodeId: string,
    dept: Department,
    decision: string,
    note?: string | null,
    version?: string | null,
  ) => {
    await api.request("POST", `${w}/episodes/${episodeId}/steps/${dept}/signoffs`, {
      body: signoffBody(decision, note, version),
    });
  };
  return {
    async episode(_projectId, episodeId) {
      const [res, nameOf] = await Promise.all([
        api.request<ApiEpisodeSteps>("GET", `${w}/episodes/${episodeId}/steps`),
        names(),
      ]);
      return episodeFromApi(res, nameOf);
    },
    async episodeFrom(read) {
      return episodeFromApi(read, await names());
    },
    async assignees(projectId) {
      const steps = await projectSteps(projectId);
      return Object.fromEntries(steps.filter((s) => s.workers.length).map((s) => [s.department, s.workers]));
    },
    async rules(projectId) {
      return (await projectSteps(projectId)).map(ruleFromStep);
    },
    async saveRule(projectId, rule) {
      const s = await api.request<ApiStep>("PUT", `${w}/projects/${projectId}/steps/${rule.dept}`, {
        body: stepPatch(rule),
      });
      return ruleFromStep(s);
    },
    async addStep(projectId, dept) {
      const s = await api.request<ApiStep>("POST", `${w}/projects/${projectId}/steps`, {
        body: { department: dept },
      });
      return ruleFromStep(s);
    },
    async applyTemplate(projectId, templateId) {
      const r = await api.request<{ items: ApiStep[] }>("POST", `${w}/projects/${projectId}/steps/template`, {
        body: { templateId },
      });
      return r.items.map(ruleFromStep);
    },
    signOff: (_projectId, episodeId, dept, o = {}) => decide(episodeId, dept, "approved", o.note, o.version),
    requestChanges: (_projectId, episodeId, dept, note, version) =>
      decide(episodeId, dept, "changes_requested", note, version),
    async assign(projectId, dept, item) {
      const r = await api.request<{ id: string }>(
        "POST",
        `${w}/projects/${projectId}/steps/${dept}/assignments`,
        {
          body: { userId: item.userId, itemType: item.itemType, itemId: item.itemId },
        },
      );
      return r.id;
    },
    async unassign(assignmentId) {
      await api.request("POST", `${w}/step-assignments/${assignmentId}/archive`);
    },
    async requestSignoff(_projectId, episodeId, dept, note) {
      const [r, nameOf] = await Promise.all([
        api.request<{ notified: string[] }>("POST", `${w}/episodes/${episodeId}/steps/${dept}/requests`, {
          body: note?.trim() ? { note: note.trim() } : {},
        }),
        names(),
      ]);
      return { notified: r.notified.map(nameOf) };
    },
    async waiting() {
      const [r, nameOf] = await Promise.all([
        api.request<{ items: ApiWaitingItem[] }>("GET", `${w}/waiting`, { query: { limit: 50 } }),
        names(),
      ]);
      return r.items.map((x) => waitingFromApi(x, nameOf));
    },
  };
}

export async function loadTemplates(api: Api, ws: string): Promise<Template[]> {
  const r = await api.request<{ items: ApiTemplate[] }>("GET", `/workspaces/${ws}/production-templates`);
  return r.items.map(templateFromApi);
}
